import React, { useState } from 'react';
import { ncService } from '../../services/api';
import authService from '../../services/authService'; // #271
import { showSuccess, showInfo } from '../../utils/toast';
import LoadingSpinner from '../LoadingSpinner';
import Button from '../ui/Button';

/**
 * Modal de confirmação da Cascata IA — REFATORADA (Auditoria de coerência,
 * Sessão 44b).
 *
 * O motor passou a ser `analyze_nc_v4`: ao recalcular, a IA RE-DERIVA a cadeia
 * inteira a partir do contexto atual (6M → porquês → CAUSA → derivados), em vez
 * de PINAR a causa raiz (o que orfanava a causa da cadeia nova — incoerente com
 * o modelo porquês-driven).
 *
 * A "régua mista" é AUTOMÁTICA no backend: o que o usuário editou à mão é
 * preservado e sinalizado (chip "revisar"), o resto é regenerado fresco. Por
 * isso os checkboxes de preservação manual saíram — o sistema já sabe o que
 * foi editado (substituídos pela régua determinística backend-side).
 *
 * Props:
 *   ncId           — ID da NC
 *   isOpen         — controla visibilidade
 *   onClose        — fecha modal (cancel ou após sucesso)
 *   onCascataOk    — callback após sucesso (parent dispara refresh)
 */
function CascataIAModal({ ncId, isOpen, onClose, onCascataOk }) {
  const [loading, setLoading] = useState(false);
  const [motivo, setMotivo] = useState('cascata_ia');
  // #271 — erro inline (não-401) pra o modal NUNCA travar no "Chamando IA...":
  // o spinner para, a mensagem aparece DENTRO do modal e o usuário escolhe
  // Cancelar ou Tentar de novo (espelha o padrão de falha de análise do #216).
  const [erro, setErro] = useState(null);

  if (!isOpen) return null;

  const handleConfirmar = async () => {
    setErro(null);
    setLoading(true);
    try {
      // Régua mista é AUTOMÁTICA no backend → não enviamos preservação manual.
      const res = await ncService.cascataIA(ncId, { motivo, camposPreservados: [] });
      showSuccess(
        `Análise recalculada a partir do contexto atual. ` +
        `${res.campos_preservados?.length || 0} campo(s) preservado(s) (suas edições à mão). ` +
        'Versão anterior recuperável via banner no topo.'
      );
      onCascataOk?.(res);
      onClose();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      // #271 — sessão expirada: o interceptor (api.js) já tenta refresh; se
      // chegou 401 aqui, a sessão é irrecuperável. Para o spinner, fecha o
      // modal e aciona a MESMA mecânica de encerramento dos interceptors
      // (#145/#215: flush do rascunho + flag + redirect pra tela de login, que
      // mostra "Sua sessão expirou"). Sem isto o modal ficava preso em
      // "Chamando IA..." pra sempre (bug #271).
      if (status === 401) {
        setLoading(false);
        onClose();
        showInfo('Sua sessão expirou — faça login novamente para continuar.');
        authService.handleSessionExpired();
        return;
      }
      if (status === 502) {
        setErro('A IA falhou ao recalcular. Sua análise NÃO foi modificada. Tente novamente em alguns minutos.');
      } else if (status === 503) {
        setErro('Recurso ainda não habilitado no servidor. Contate o administrador.');
      } else if (status === 409) {
        setErro('A NC precisa estar em rascunho para recalcular.');
      } else {
        setErro(`Não foi possível recalcular: ${detail}`);
      }
    } finally {
      // #271 — garante que o spinner SEMPRE para, em qualquer caminho de erro.
      setLoading(false);
    }
  };

  // #271 — fechar limpa o erro inline pra a próxima abertura começar limpa.
  const handleClose = () => {
    setErro(null);
    onClose();
  };

  return (
    <div
      className="cascata-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cascata-modal-title"
      onClick={loading ? undefined : handleClose}
    >
      <div
        className="cascata-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cascata-modal-title"><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>autorenew</span>Recalcular análise com IA</h3>

        <div className="cascata-modal-warning">
          <strong>Atenção:</strong> esta operação re-deriva TODA a análise a
          partir do contexto atual — 6M, porquês e <strong>causa raiz inclusive</strong> —
          mais as ações e o plano 5W2H. Demora ~60-90s. A versão atual é gravada
          como snapshot — você pode reverter depois.
        </div>

        <div className="cascata-modal-info">
          <strong><span className="material-symbols-outlined" style={{ fontSize: '15px', verticalAlign: 'middle', marginRight: '4px' }}>info</span>O que você editou à mão é preservado:</strong> ações,
          hipótese, conclusão, impacto ou contenção que você ajustou manualmente
          NÃO são sobrescritos — ficam preservados e marcados como "revisar"
          (podem não refletir a análise nova). O restante é regenerado fresco.
        </div>

        <div className="cascata-modal-field">
          <label htmlFor="cascata-motivo">Motivo (opcional, pra auditoria):</label>
          <input
            id="cascata-motivo"
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={loading}
            placeholder="ex: contexto atualizado"
          />
        </div>

        {/* #271 — erro inline (não-401): o modal nunca trava; o usuário lê o
            que houve e decide Cancelar ou Tentar de novo. */}
        {erro && (
          <div className="cascata-modal-warning" role="alert" style={{ borderColor: '#fca5a5', background: '#fef2f2', color: '#991b1b' }}>
            <strong><span className="material-symbols-outlined" style={{ fontSize: '15px', verticalAlign: 'middle', marginRight: '4px' }}>error</span>{erro}</strong>
          </div>
        )}

        <div className="cascata-modal-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            icon={loading ? undefined : (erro ? 'refresh' : 'autorenew')}
            onClick={handleConfirmar}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" />
                <span>Chamando IA...</span>
              </>
            ) : (erro ? 'Tentar de novo' : 'Recalcular agora')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CascataIAModal;
